export type GoogleOAuthContext = {
  action?: 'external_login';
  keepToken?: boolean;
};

type GoogleOAuthParams = {
  redirectUri?: string;
  context?: GoogleOAuthContext;
};

type StoredOAuthState = {
  nonce: string;
  context: GoogleOAuthContext;
  /** PKCE (RFC 7636): guardado junto do nonce e enviado ao telli na troca do code. */
  codeVerifier?: string;
};

/** O que o callback do Google precisa para concluir o login nesta aba. */
export type OAuthHandshake = {
  context: GoogleOAuthContext;
  codeVerifier?: string;
};

export const REDIRECT_LOGIN = `${window.location.origin}/login`;

export const OAUTH_STATE_STORAGE_KEY = '@googleOAuthState';

export const EXTERNAL_LOGIN_START_PARAM = 'oauth_start';

const NONCE_BYTES = 16;
const CODE_VERIFIER_BYTES = 32;

export function generateOAuthNonce(): string {
  const bytes = new Uint8Array(NONCE_BYTES);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  // Em base64 o "=" só existe como padding final, então remover todos equivale a tirar o sufixo,
  // sem regex com backtracking (Sonar S5852).
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/** code_verifier com 43 caracteres url-safe (32 bytes), dentro do intervalo da RFC 7636 §4.1. */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(CODE_VERIFIER_BYTES);
  window.crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

/**
 * S256: BASE64URL(SHA-256(ASCII(code_verifier))), RFC 7636 §4.2. Devolve `undefined` quando o
 * navegador não expõe `crypto.subtle` (contexto não seguro), para o login seguir sem PKCE.
 */
export async function createCodeChallenge(codeVerifier: string): Promise<string | undefined> {
  const subtle = window.crypto?.subtle;
  if (!subtle) return undefined;

  const digest = await subtle.digest(
    'SHA-256',
    Uint8Array.from(codeVerifier, (char) => char.charCodeAt(0)),
  );
  return toBase64Url(new Uint8Array(digest));
}

/**
 * PKCE ligado por ambiente em tempo de build (`REACT_APP_GOOGLE_OAUTH_PKCE` em `.env.development` /
 * `.env.production`), como `REACT_APP_LOGIN_METHOD`. Depende do telli aceitar `codeVerifier` em
 * /login-google-token (telli #14138): ligar só com o backend do ambiente já no ar.
 */
export function isPkceEnabled(): boolean {
  return process.env.REACT_APP_GOOGLE_OAUTH_PKCE === 'true';
}

/**
 * Monta a URL de autorização do Google. O state leva só o nonce (anti-CSRF); o contexto e, com o
 * PKCE ligado, o code_verifier ficam na sessionStorage desta aba. Não pede `access_type=offline`:
 * o telli só usa o access_token para ler o userinfo, então um refresh_token seria credencial
 * emitida sem uso.
 */
export async function getGoogleOAuthUrl({ redirectUri, context }: GoogleOAuthParams = {}): Promise<string> {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';
  const scopes = ['https://www.googleapis.com/auth/userinfo.email', 'https://www.googleapis.com/auth/userinfo.profile'];
  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

  const nonce = generateOAuthNonce();
  const stored: StoredOAuthState = { nonce, context: context ?? {} };

  let codeChallenge: string | undefined;
  if (isPkceEnabled()) {
    const codeVerifier = generateCodeVerifier();
    try {
      codeChallenge = await createCodeChallenge(codeVerifier);
    } catch (error) {
      // Falha no digest (ex.: crypto.subtle indisponivel em runtime) nao pode travar o login:
      // segue sem PKCE, como no caso de subtle ausente.
      codeChallenge = undefined;
    }
    if (codeChallenge) stored.codeVerifier = codeVerifier;
  }

  sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, JSON.stringify(stored));

  authUrl.searchParams.append('client_id', clientId);
  authUrl.searchParams.append('redirect_uri', redirectUri || REDIRECT_LOGIN);
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('scope', scopes.join(' '));
  authUrl.searchParams.append('state', nonce);

  if (codeChallenge) {
    authUrl.searchParams.append('code_challenge', codeChallenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
  }

  return authUrl.toString();
}

function readStoredState(state: unknown): StoredOAuthState | null {
  if (typeof state !== 'string' || !state) return null;

  try {
    const stored: Partial<StoredOAuthState> | null = JSON.parse(
      sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY) ?? 'null',
    );
    if (!stored || stored.nonce !== state) return null;
    return { nonce: stored.nonce, context: stored.context ?? {}, codeVerifier: stored.codeVerifier };
  } catch (error) {
    return null;
  }
}

/**
 * Recupera o contexto do login pelo nonce recebido no callback. Retorna null quando o state
 * está ausente ou não corresponde ao nonce gerado nesta aba (callback forjado / login CSRF).
 */
export function readOAuthState(state: unknown): GoogleOAuthContext | null {
  return readStoredState(state)?.context ?? null;
}

/** Mesma validação de `readOAuthState`, devolvendo também o code_verifier para a troca. */
export function readOAuthHandshake(state: unknown): OAuthHandshake | null {
  const stored = readStoredState(state);
  if (!stored) return null;
  return { context: stored.context, codeVerifier: stored.codeVerifier };
}

export function clearOAuthState(): void {
  sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
}

/**
 * URL da janela de login externo (iframe / renovação de sessão). A janela abre primeiro o próprio
 * backoffice, que gera o nonce na sua sessionStorage (first-party) e só então redireciona ao Google.
 * Assim o nonce não depende de a sessionStorage do opener ser copiada (iframe cross-site é particionado).
 */
export function getExternalLoginStartUrl({ keepToken }: Pick<GoogleOAuthContext, 'keepToken'> = {}): string {
  const url = new URL(REDIRECT_LOGIN);
  url.searchParams.append(EXTERNAL_LOGIN_START_PARAM, 'external_login');
  if (keepToken) url.searchParams.append('keepToken', 'true');
  return url.toString();
}
